import get from 'lodash.get';
import PropTypes from 'prop-types';
import serverVars from 'server-vars';
import { graphql } from 'react-relay';
import { defineMessages } from 'react-intl';
import { supportedAmpLocales } from 'dibs-intl/exports/constants';

export class PdpHead {
    constructor(props) {
        this.props = props;
        this.item = this.props.viewer.item;
        const isPdpUrlTranslationEnabled = this.props.viewer.isPdpUrlTranslationEnabled;
        this.pdpUrl = isPdpUrlTranslationEnabled ? this.item.localizedPdpUrl : this.item.pdpURL;
        this.intl = this.props.intl;
        this.baseUrl = this.props.intlVariables.localeDomain;
        this.canonicalUrl = `https://${this.baseUrl}${this.pdpUrl}`;
        this.msgs = defineMessages({
            siteName: {
                id: 'pdpHead.siteName',
                defaultMessage: '1stdibs.com',
            },
        });
        const media = get(props, 'viewer.item.photos[0].versions') || [];
        const image = media.find(p => get(p, 'type.name') === 'large') || {};
        this.imageUrl = image.webPath;
    }

    metaFacebook() {
        return [
            { name: 'fb:admins', content: serverVars.get('config.facebookAdmin') },
            { name: 'fb:app_id', content: serverVars.get('config.facebookAppId') },
            { name: 'fb:page_id', content: serverVars.get('config.facebookPageId') },
        ];
    }

    metaProductPrice() {
        const { pdpMeta = {} } = this.item;

        if (!pdpMeta.productPriceAmount || !pdpMeta.productPriceCurrency) {
            return [];
        }

        return [
            { property: 'product:price:amount', content: pdpMeta.productPriceAmount },
            { property: 'product:price:currency', content: pdpMeta.productPriceCurrency },
        ];
    }

    metaTwitter() {
        const { pdpMeta = {} } = this.item;
        return [
            { name: 'twitter:card', content: 'summary' },
            { name: 'twitter:site', content: '@1stdibs' },
            { name: 'twitter:url', content: this.canonicalUrl },
            { name: 'twitter:title', content: pdpMeta.ogTitle },
            { name: 'twitter:description', content: pdpMeta.description },
            { name: 'twitter:image', content: this.imageUrl },
        ];
    }

    title() {
        const { pdpMeta = {} } = this.item;
        return pdpMeta.title;
    }

    meta(metaMore = []) {
        return [
            this.metaDescription(),
            ...this.metaFacebook(),
            ...this.metaOg(),
            ...this.metaTwitter(),
            ...this.metaProductPrice(),
            ...metaMore,
        ];
    }

    metaDescription() {
        const { pdpMeta = {} } = this.item;
        return { name: 'description', content: pdpMeta.description };
    }

    metaOg() {
        return [
            { name: 'og:site_name', content: this.intl.formatMessage(this.msgs.siteName) },
            { name: 'og:title', content: get(this.item, 'pdpMeta.ogTitle') },
            { name: 'og:url', content: this.canonicalUrl },
            { name: 'og:type', content: 'website' },
            { name: 'og:image', content: this.imageUrl },
        ];
    }

    link() {
        const { localeDomain, locale, languageAnnotations = [] } = this.props.intlVariables;
        const { viewer } = this.props;
        const { item } = viewer;
        const { isAvailable } = item;

        const links = [
            { rel: 'canonical', href: this.canonicalUrl },
            { rel: 'dns-prefetch', href: serverVars.get('config.cdnDomain') },
            {
                rel: 'shortcut icon',
                type: 'image/x-icon',
                href: 'https://www.1stdibs.com/favicon.ico',
            },
        ];
        const isListingListed = !get(item, 'isListingNotListed') || get(item, 'seller.isInternal');

        // if the current domain supports amp pages & the item is listed, include the amphtml link
        if (supportedAmpLocales.includes(locale) && isListingListed) {
            links.push({
                rel: 'amphtml',
                href: `https://${localeDomain}/amp${this.pdpUrl}`,
            });
        }

        // if item is available we should add language annotation as meta tags
        if (isAvailable) {
            // hrefLang tags generated in language annotations middleware
            languageAnnotations.forEach(({ href, hrefLang }) => {
                links.push({
                    rel: 'alternate',
                    href,
                    hrefLang,
                });
            });
        }
        return links;
    }

    microdata() {
        const { microdata } = this.item;
        return microdata
            ? {
                  type: 'application/ld+json',
                  innerHTML: microdata,
              }
            : {};
    }

    /**
     * @description Only use this with mobile. At some point DBL will handle preload links.
     */
    mobileOnlylinkPreload() {
        const fonts = [
            'MillerDisplay/MillerDisplay-Light.woff',
            'MillerDisplay/MillerDisplay-LightItalic.woff',
            'ProximaNova/ProximaNova-Light.woff',
            'ProximaNova/ProximaNova-Sbold.woff',
        ];
        return fonts.map(font => ({
            rel: 'preload',
            as: 'font',
            crossorigin: 'anonymous', // https://developer.mozilla.org/en-US/docs/Web/HTML/Preloading_content#Cross-origin_fetches
            href: `https://a.1stdibscdn.com/dist/fonts/${font}`,
        }));
    }
}

PdpHead.propTypes = {
    intlVariables: PropTypes.shape({
        locale: PropTypes.string,
        localeDomain: PropTypes.string,
        languageAnnotations: PropTypes.array,
    }).isRequired,
};

export const itemFragment = graphql`
    fragment PdpHead_viewer on Viewer {
        isPdpUrlTranslationEnabled: isEnabled(key: "translatedPdpUrls")
    }

    fragment PdpHead_item on Item {
        isAvailable
        microdata
        pdpURL
        localizedPdpUrl
        pdpMeta {
            title
            description
            ogTitle
            productPriceAmount
            productPriceCurrency
        }
        photos {
            versions {
                webPath
                type {
                    name
                }
            }
        }
        isListingNotListed
        seller {
            isInternal
        }
    }
`;
